import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Zap, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const iconMap = { Clock, MapPin, Zap };

const CTASection = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();

  return (
    <section className="relative py-32 border-t border-[#21262d]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-2">
                {ui.contactLabel}
              </span>
              <span className="font-mono text-[11px] tracking-[0.15em] text-[#7d8590] uppercase block mb-6">
                {ui.letsBuild}
              </span>
              <h2 className="text-[clamp(36px,4.5vw,64px)] font-black leading-[0.95] tracking-[-0.02em]">
                <span className="text-[#e6edf3]">{ui.gotPlatformIdea[0]}</span>
                <span className="text-[#3b82f6]">{ui.gotPlatformIdea[1]}</span>
              </h2>
              <p className="font-mono text-[12px] leading-[1.7] text-[#7d8590] mt-6 max-w-md">
                {ui.ctaDescription}
              </p>
            </motion.div>
          </div>

          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-3 gap-3"
            >
              {content.contactCards.map((card) => {
                const IconComponent = iconMap[card.icon];
                return (
                  <div key={card.label} className="bg-[#161b22] border border-[#21262d] rounded-lg p-4 hover:border-[#3b82f6]/30 transition-colors duration-300">
                    <IconComponent size={16} className="text-[#3b82f6] mb-3" />
                    <p className="font-mono text-[9px] tracking-[0.15em] text-[#7d8590] uppercase mb-1">{card.label}</p>
                    <p className="text-sm font-bold text-[#e6edf3]">{card.value}</p>
                    {card.sub && <p className="font-mono text-[10px] text-[#7d8590] mt-0.5">{card.sub}</p>}
                  </div>
                );
              })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#161b22] border border-[#21262d] rounded-lg p-6"
            >
              <p className="font-mono text-[10px] tracking-[0.15em] text-[#7d8590] uppercase mb-4">{ui.startConversation}</p>
              <a
                href="mailto:info@go2digital.com?subject=Partnership%20Inquiry"
                className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase bg-[#3b82f6] text-white px-6 py-3 rounded-full hover:bg-[#2563eb] transition-colors duration-300 font-semibold"
              >
                <Send size={13} />
                {ui.sendMessage}
              </a>
              <p className="font-mono text-[10px] text-[#7d8590] mt-4">
                {ui.emailDirectly} <a href="mailto:info@go2digital.com" className="text-[#3b82f6] hover:underline">info@go2digital.com</a>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#161b22] border border-[#21262d] rounded-lg p-6"
            >
              <p className="font-mono text-[10px] tracking-[0.15em] text-[#7d8590] uppercase mb-4">{ui.interestedInApproach}</p>
              <Link
                to={localPath('/about/')}
                className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#3b82f6] border border-[#3b82f6]/50 px-6 py-3 rounded-full hover:bg-[#3b82f6]/10 transition-all duration-300 font-medium"
              >
                <ArrowRight size={13} />
                {ui.learnHowWeThink}
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
